/**
 * Admin -- Audit/Timeline (Wave 1b W1b-30)
 *
 * Paginated chronological view of every audit_chain entry for the tenant,
 * with filters (actor, event type, severity, date range, free-text search).
 * Cursor-based infinite scroll. Detail sheet on row click. Per-row
 * "Verify from here" runs a bounded chain walk and shows walked/brokenAt.
 *
 * Backend: /api/v1/admin/audit/timeline (W1b-30 backend).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
  Search,
  Download,
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Hash,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { relativeTime } from '@/lib/format';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';

export const Route = createFileRoute('/admin/audit/')({
  component: TimelinePage,
});

// ─── Types ────────────────────────────────────────────────────────────────

type Severity = 'medium' | 'high' | 'critical';

interface TimelineItem {
  id: string;
  sequenceNumber: string;
  eventType: string;
  actorId: string | null;
  actorType: string;
  actorEmail: string | null;
  resourceType: string;
  resourceId: string | null;
  severity: Severity;
  sensitivity: string;
  exportControl: string;
  ipAddress: string | null;
  recordHash: string;
  previousHash: string;
  createdAt: string;
  eventDataPreview: Record<string, unknown> | null;
}

interface TimelineDetail extends TimelineItem {
  userAgent: string | null;
  requestId: string | null;
  eventData: unknown;
  prevId: string | null;
  nextId: string | null;
}

interface TimelinePage {
  items: TimelineItem[];
  cursor: string | null;
  total: number;
}

interface VerifyResult {
  verified: boolean;
  brokenAt: number | null;
  walked: number;
  durationMs: number;
  brokenReason: string | null;
}

interface IntegrityStatus {
  chainHeadSeq: number;
  chainHeadHash: string;
  chainHeadExists: boolean;
  lastWitnessAt: string | null;
  lastWitnessSeq: number | null;
  isHealthy: boolean;
  brokenChainBanner: { active: boolean; brokenAt?: number; reason?: string };
  witnessKeyId: string;
}

// ─── Component ────────────────────────────────────────────────────────────

function TimelinePage() {
  const [searchText, setSearchText] = useState('');
  const [actorEmail, setActorEmail] = useState('');
  const [eventType, setEventType] = useState<string>('all');
  const [resourceType, setResourceType] = useState<string>('all');
  const [severity, setSeverity] = useState<'all' | Severity>('all');
  const [sinceDays, setSinceDays] = useState<string>('all');
  const [viewing, setViewing] = useState<TimelineDetail | null>(null);
  const [verifyDialog, setVerifyDialog] = useState<{
    fromSeq: number;
    result: VerifyResult | null;
    pending: boolean;
  } | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Query string for pagination -- TanStack derives the queryKey from this.
  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (searchText.trim()) p.set('searchText', searchText.trim());
    if (actorEmail.trim()) p.set('actorEmail', actorEmail.trim());
    if (eventType !== 'all') p.set('eventType', eventType);
    if (resourceType !== 'all') p.set('resourceType', resourceType);
    if (severity !== 'all') p.set('severity', severity);
    if (sinceDays !== 'all') {
      const d = new Date();
      d.setDate(d.getDate() - Number(sinceDays));
      p.set('since', d.toISOString());
    }
    p.set('limit', '50');
    return p;
  }, [searchText, actorEmail, eventType, resourceType, severity, sinceDays]);

  // ── Status banner ──
  const statusQuery = useQuery({
    queryKey: ['audit', 'integrity', 'status'],
    queryFn: () => apiClient.get<IntegrityStatus>('/admin/audit/integrity/status'),
    refetchInterval: 60_000,
  });

  // ── Event type catalog ──
  const eventTypesQuery = useQuery({
    queryKey: ['audit', 'event-types'],
    queryFn: () =>
      apiClient.get<{ eventTypes: string[]; cached: boolean }>(
        '/admin/audit/timeline/event-types'
      ),
  });

  // ── Timeline (infinite) ──
  const timelineQuery = useInfiniteQuery({
    queryKey: ['audit', 'timeline', queryParams.toString()],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const p = new URLSearchParams(queryParams);
      if (pageParam) p.set('cursor', pageParam);
      return apiClient.get<TimelinePage>(`/admin/audit/timeline?${p.toString()}`);
    },
    getNextPageParam: (last) => last.cursor,
  });

  // ── Detail fetch ──
  const detailQuery = useQuery({
    queryKey: ['audit', 'timeline', 'detail', viewing?.id],
    queryFn: () =>
      viewing?.id
        ? apiClient.get<TimelineDetail>(`/admin/audit/timeline/${viewing.id}`)
        : Promise.reject(new Error('no id')),
    enabled: !!viewing?.id && !viewing?.eventData, // only fetch full detail
  });

  useEffect(() => {
    if (detailQuery.data && viewing && !viewing.eventData) {
      setViewing(detailQuery.data);
    }
  }, [detailQuery.data, viewing]);

  // ── Verify-from-here ──
  const verifyMutation = useMutation({
    mutationFn: async (fromSeq: number) =>
      apiClient.post<VerifyResult>('/admin/audit/timeline/verify-from-here', {
        fromSeq,
        maxRecords: 500,
      }),
    onSuccess: (result, fromSeq) => {
      setVerifyDialog({ fromSeq, result, pending: false });
      if (result.verified) {
        toast.success(`Chain verified from seq ${fromSeq} (${result.walked} records)`);
      } else {
        toast.error(`Chain BROKEN at seq ${result.brokenAt}: ${result.brokenReason}`);
      }
    },
    onError: (err) => {
      toast.error(`Verify failed: ${(err as Error).message}`);
      setVerifyDialog(null);
    },
  });

  // ── Infinite scroll sentinel ──
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        timelineQuery.hasNextPage &&
        !timelineQuery.isFetchingNextPage
      ) {
        timelineQuery.fetchNextPage();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [timelineQuery.hasNextPage, timelineQuery.isFetchingNextPage, timelineQuery]);

  const items = timelineQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const banner = statusQuery.data?.brokenChainBanner;
  const eventTypes = eventTypesQuery.data?.eventTypes ?? [];

  // Distinct resource types from currently-loaded items.
  const resourceTypes = useMemo(() => {
    const set = new Set<string>();
    items.forEach((i) => set.add(i.resourceType));
    return Array.from(set).sort();
  }, [items]);

  function handleExportCsv() {
    const rows = [
      'sequence,eventType,actor,resourceType,resourceId,severity,createdAt,recordHash',
      ...items.map((i) =>
        [
          i.sequenceNumber,
          i.eventType,
          i.actorEmail ?? '',
          i.resourceType,
          i.resourceId ?? '',
          i.severity,
          i.createdAt,
          i.recordHash,
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(',')
      ),
    ].join('\n');
    const blob = new Blob([rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-timeline-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${items.length} rows to CSV`);
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5">
      {/* Integrity banner */}
      <div
        className={cn(
          'flex items-center gap-3 p-3 border rounded-md',
          banner?.active
            ? 'bg-red-500/5 border-red-500/30'
            : 'bg-emerald-500/5 border-emerald-500/30'
        )}
      >
        {banner?.active ? (
          <ShieldAlert className="h-4 w-4 shrink-0 text-red-500" />
        ) : (
          <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-500" />
        )}
        <div className="flex-1 min-w-0 text-xs">
          <span className="font-medium">
            {banner?.active
              ? `Hash chain BROKEN at seq ${banner.brokenAt}`
              : 'Hash chain verified -- audit trail intact'}
          </span>
          {statusQuery.data && (
            <span className="text-muted-foreground ml-2">
              chain head seq {statusQuery.data.chainHeadSeq.toLocaleString()}
              {statusQuery.data.lastWitnessAt && (
                <>
                  {' '}
                  • last witness {relativeTime(statusQuery.data.lastWitnessAt)}
                </>
              )}
              {' '}• key {statusQuery.data.witnessKeyId}
            </span>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">Admin Audit Trail</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Every admin action -- feature toggles, security changes, key
            rotations -- captured immutably.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs gap-1.5"
          onClick={handleExportCsv}
          title="Export the currently-loaded rows to CSV"
        >
          <Download className="h-3 w-3" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search event payload…"
            className="pl-7 h-8 text-xs"
          />
        </div>

        <Input
          value={actorEmail}
          onChange={(e) => setActorEmail(e.target.value)}
          placeholder="Actor email"
          className="w-44 h-8 text-xs"
        />

        <Select value={eventType} onValueChange={setEventType}>
          <SelectTrigger className="w-48 h-8 text-xs">
            <SelectValue placeholder="Event type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All event types</SelectItem>
            {eventTypes.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={resourceType} onValueChange={setResourceType}>
          <SelectTrigger className="w-40 h-8 text-xs">
            <SelectValue placeholder="Resource type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All resources</SelectItem>
            {resourceTypes.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={severity}
          onValueChange={(v) => setSeverity(v as 'all' | Severity)}
        >
          <SelectTrigger className="w-32 h-8 text-xs">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severity</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sinceDays} onValueChange={setSinceDays}>
          <SelectTrigger className="w-32 h-8 text-xs">
            <SelectValue placeholder="Time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="1">Last 24h</SelectItem>
            <SelectItem value="7">Last 7d</SelectItem>
            <SelectItem value="30">Last 30d</SelectItem>
            <SelectItem value="90">Last 90d</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs gap-1.5"
          onClick={() => timelineQuery.refetch()}
          title="Reload timeline"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </Button>
      </div>

      {/* Total + result count */}
      <div className="text-xs text-muted-foreground">
        {timelineQuery.isLoading
          ? 'Loading…'
          : `Showing ${items.length} of ${timelineQuery.data?.pages[0]?.total ?? 0} events`}
      </div>

      {/* Table */}
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/30">
            <tr className="text-left">
              <th className="p-2 font-medium">When</th>
              <th className="p-2 font-medium">Actor</th>
              <th className="p-2 font-medium">Event</th>
              <th className="p-2 font-medium">Resource</th>
              <th className="p-2 font-medium">Severity</th>
              <th className="p-2 font-medium text-right">Seq</th>
              <th className="p-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr
                key={i.id}
                className="border-t hover:bg-muted/30 cursor-pointer"
                onClick={() => setViewing({ ...(i as TimelineDetail), eventData: undefined })}
              >
                <td className="p-2">{relativeTime(i.createdAt)}</td>
                <td className="p-2">{i.actorEmail ?? <span className="italic text-muted-foreground">{i.actorType}</span>}</td>
                <td className="p-2 font-mono">{i.eventType}</td>
                <td className="p-2">{i.resourceType}</td>
                <td className="p-2">
                  <SeverityBadge severity={i.severity} />
                </td>
                <td className="p-2 text-right font-mono">{i.sequenceNumber}</td>
                <td className="p-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-[11px] gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      const seq = Number(i.sequenceNumber);
                      setVerifyDialog({ fromSeq: seq, result: null, pending: true });
                      verifyMutation.mutate(seq);
                    }}
                    title="Verify chain integrity from this row forward (up to 500 records)"
                  >
                    <ShieldCheck className="h-3 w-3" />
                    Verify from here
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && !timelineQuery.isLoading && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-muted-foreground">
                  No audit events match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {timelineQuery.hasNextPage && (
        <div ref={sentinelRef} className="flex justify-center py-4">
          {timelineQuery.isFetchingNextPage ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <span className="text-xs text-muted-foreground">Scroll to load more</span>
          )}
        </div>
      )}

      {/* Detail sheet */}
      <Sheet open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-mono text-base">{viewing?.eventType}</SheetTitle>
            <SheetDescription>
              Sequence {viewing?.sequenceNumber} • {viewing && relativeTime(viewing.createdAt)}
            </SheetDescription>
          </SheetHeader>

          {viewing && (
            <div className="mt-4 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Actor" value={viewing.actorEmail ?? viewing.actorType} />
                <Field label="Severity"><SeverityBadge severity={viewing.severity} /></Field>
                <Field label="Resource type" value={viewing.resourceType} />
                <Field label="Resource ID" value={viewing.resourceId ?? '—'} mono />
                <Field label="Sensitivity" value={viewing.sensitivity} />
                <Field label="Export control" value={viewing.exportControl} />
                <Field label="IP address" value={viewing.ipAddress ?? '—'} mono />
                <Field label="Request ID" value={viewing.requestId ?? '—'} mono />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label className="text-xs">Hash chain</Label>
                <div className="space-y-1">
                  <div className="flex items-start gap-2 font-mono text-[11px]">
                    <Hash className="h-3 w-3 mt-0.5 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="text-muted-foreground">record_hash</div>
                      <div className="break-all">{viewing.recordHash}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 font-mono text-[11px]">
                    <Hash className="h-3 w-3 mt-0.5 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="text-muted-foreground">previous_hash</div>
                      <div className="break-all">{viewing.previousHash}</div>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label className="text-xs">Event payload</Label>
                <pre className="p-2 bg-muted/50 rounded text-[11px] font-mono overflow-x-auto">
                  {JSON.stringify(viewing.eventData ?? viewing.eventDataPreview ?? {}, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Verify-from-here dialog */}
      <Dialog
        open={!!verifyDialog}
        onOpenChange={(o) => !o && setVerifyDialog(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Verify chain from sequence {verifyDialog?.fromSeq}
            </DialogTitle>
            <DialogDescription>
              Walks up to 500 records forward from this sequence, recomputing
              every hash and comparing against the stored value.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            {verifyDialog?.pending || verifyMutation.isPending ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Walking chain…</span>
              </div>
            ) : verifyDialog?.result ? (
              <>
                <div className="flex items-center gap-2">
                  {verifyDialog.result.verified ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <span className="font-medium">
                    {verifyDialog.result.verified
                      ? 'Chain intact'
                      : `Chain BROKEN at sequence ${verifyDialog.result.brokenAt}`}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Walked:</span>{' '}
                    {verifyDialog.result.walked} records
                  </div>
                  <div>
                    <span className="text-muted-foreground">Duration:</span>{' '}
                    {verifyDialog.result.durationMs}ms
                  </div>
                  {verifyDialog.result.brokenReason && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Reason:</span>{' '}
                      <code>{verifyDialog.result.brokenReason}</code>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setVerifyDialog(null)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: Severity }) {
  if (severity === 'critical') {
    return (
      <Badge variant="outline" className="border-red-500/40 bg-red-500/10 text-red-600 gap-1 h-5 text-[10px]">
        <AlertTriangle className="h-3 w-3" />
        Critical
      </Badge>
    );
  }
  if (severity === 'high') {
    return (
      <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-600 h-5 text-[10px]">
        High
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-slate-500/40 bg-slate-500/10 text-slate-600 h-5 text-[10px]">
      Medium
    </Badge>
  );
}

function Field({
  label,
  value,
  mono,
  children,
}: {
  label: string;
  value?: string;
  mono?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('text-sm', mono && 'font-mono break-all')}>
        {children ?? value}
      </div>
    </div>
  );
}
